import React, { useState } from "react";
import { colors, spacing, radius } from "@forge/ui";

type Exercise = {
  id: string;
  name: string;
  sets?: number;
  reps?: number;
  rest?: number;
};

type GuidedResult = {
  setsCompleted: number;
  reps?: number;
  weight?: number;
};

type GuidedWorkoutProps = {
  exercises: Exercise[];
  onComplete: (results: Record<string, GuidedResult>) => void;
};

export default function GuidedWorkout({
  exercises,
  onComplete,
}: GuidedWorkoutProps) {
  const [exIdx, setExIdx] = useState(0);
  const [setIdx, setSetIdx] = useState(0);
  const [reps, setReps] = useState(exercises[0]?.reps?.toString() ?? "");
  const [weight, setWeight] = useState("");
  const [results, setResults] = useState<Record<string, GuidedResult>>({});

  const exercise = exercises[exIdx];
  const totalSets = exercise?.sets ?? 3;
  const currentSet = setIdx + 1;

  function logSet() {
    const ex = exercises[exIdx];
    const prev = results[ex.id] || { setsCompleted: 0 };
    const updated = {
      ...results,
      [ex.id]: {
        setsCompleted: prev.setsCompleted + 1,
        reps: Number(reps) || undefined,
        weight: Number(weight) || undefined,
      },
    };
    setResults(updated);

    if (setIdx + 1 < totalSets) {
      setSetIdx(setIdx + 1);
      setReps(exercises[exIdx]?.reps?.toString() ?? "");
      setWeight("");
    } else if (exIdx + 1 < exercises.length) {
      setExIdx(exIdx + 1);
      setSetIdx(0);
      const nextEx = exercises[exIdx + 1];
      setReps(nextEx.reps?.toString() ?? "");
      setWeight("");
    } else {
      onComplete(updated);
    }
  }

  function finishEarly() {
    onComplete(results);
  }

  return (
    <div style={styles.container}>
      <div style={styles.progress}>
        Exercise {exIdx + 1} of {exercises.length}
      </div>
      <div style={styles.exerciseName}>{exercise.name}</div>
      <div style={styles.setInfo}>
        Set {currentSet} of {totalSets}
        {exercise.reps != null && ` · Target: ${exercise.reps} reps`}
      </div>

      <div style={styles.form}>
        <div style={styles.field}>
          <label htmlFor="reps" style={styles.label}>
            Reps
          </label>
          <input
            id="reps"
            type="number"
            min={0}
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={styles.field}>
          <label htmlFor="weight" style={styles.label}>
            Weight
          </label>
          <input
            id="weight"
            type="number"
            min={0}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            style={styles.input}
          />
        </div>
      </div>

      <div style={styles.actions}>
        <button onClick={logSet} style={styles.logBtn}>
          Log Set
        </button>
        <button onClick={finishEarly} style={styles.finishBtn}>
          Finish Early
        </button>
      </div>
    </div>
  );
}

const styles: { [k: string]: React.CSSProperties } = {
  container: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: radius.md,
    padding: spacing.lg,
    display: "flex",
    flexDirection: "column",
    gap: spacing.md,
  },
  progress: {
    fontSize: 12,
    color: colors.textMuted,
  },
  exerciseName: {
    fontSize: 20,
    fontWeight: 700,
  },
  setInfo: {
    fontSize: 14,
    color: colors.textDim,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.sm,
  },
  field: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
  },
  label: {
    width: 70,
    fontSize: 13,
    color: colors.textMuted,
  },
  input: {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.bg,
    color: colors.text,
    fontSize: 14,
  },
  actions: {
    display: "flex",
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  logBtn: {
    flex: 1,
    padding: `${spacing.md}px ${spacing.lg}px`,
    borderRadius: radius.md,
    border: "none",
    backgroundColor: colors.accent,
    color: colors.text,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
  finishBtn: {
    padding: `${spacing.md}px ${spacing.lg}px`,
    borderRadius: radius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: "transparent",
    color: colors.textMuted,
    cursor: "pointer",
    fontSize: 14,
  },
};
